// components/ProjectDetailsDialog.vue
<template>
  <v-dialog
      v-model="dialog"
      max-width="1000px"
      scrollable
  >
    <v-card v-if="project">
      <v-card-title class="headline grey lighten-2">
        <v-avatar size="40" class="mr-3" v-if="project.avatarUrl">
          <v-img :src="project.avatarUrl"></v-img>
        </v-avatar>
        <v-icon v-else size="40" class="mr-3">mdi-gitlab</v-icon>
        {{ project.name }}
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-4">
        <v-row v-if="loading">
          <v-col cols="12" class="text-center">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </v-col>
        </v-row>

        <template v-else>
          <!-- 프로젝트 통계 카드 -->
          <v-row>
            <v-col cols="12" md="3">
              <v-card outlined class="stat-card">
                <v-card-text class="text-center">
                  <div class="text-h4 primary--text">
                    {{ projectDetails?.extendedStats?.mergeRequestStats?.open || 0 }}
                  </div>
                  <div class="caption grey--text text-uppercase">Ongoing MR</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card outlined class="stat-card">
                <v-card-text class="text-center">
                  <div class="text-h4 success--text">
                    {{ projectDetails?.extendedStats?.commitStats?.weekly_commits || 0 }}
                  </div>
                  <div class="caption grey--text text-uppercase">Weekly Commits</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card outlined class="stat-card">
                <v-card-text class="text-center">
                  <div class="text-h4 info--text">
                    {{ projectDetails?.extendedStats?.pipelineStats?.success || 0 }}
                  </div>
                  <div class="caption grey--text text-uppercase">Successful Pipelines</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card outlined class="stat-card">
                <v-card-text class="text-center">
                  <div class="text-h4 warning--text">
                    {{ projectDetails?.statistics?.open_issues_count || 0 }}
                  </div>
                  <div class="caption grey--text text-uppercase">Open Issues</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- 최근 Merge Requests -->
          <v-row class="mt-4">
            <v-col cols="12">
              <v-card outlined>
                <v-card-title class="subtitle-1 d-flex align-center">
                  <v-icon left>mdi-source-merge</v-icon>
                  Recent Merge Requests
                  <v-spacer></v-spacer>
                  <v-btn
                      small
                      text
                      color="primary"
                      :href="`${project.webUrl}/-/merge_requests`"
                      target="_blank"
                  >
                    View All MRs
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <v-list dense>
                    <v-list-item
                        v-for="mr in projectDetails?.recentMergeRequests"
                        :key="mr.id"
                        :href="`${project.webUrl}/-/merge_requests/${mr.iid}`"
                        target="_blank"
                    >
                      <v-list-item-content>
                        <v-list-item-title class="subtitle-2">
                          {{ mr.title }}
                          <v-chip
                              x-small
                              :color="getMrStateColor(mr.state)"
                              text-color="white"
                              class="ml-2"
                          >
                            {{ mr.state }}
                          </v-chip>
                        </v-list-item-title>
                        <v-list-item-subtitle class="caption">
                          !{{ mr.iid }} by {{ mr.author.name }} •
                          {{ formatDate(mr.created_at) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- 최근 파이프라인 -->
          <v-row class="mt-4">
            <v-col cols="12">
              <v-card outlined>
                <v-card-title class="subtitle-1 d-flex align-center">
                  <v-icon left>mdi-pipe</v-icon>
                  Recent Pipelines
                  <v-spacer></v-spacer>
                  <v-btn
                      small
                      text
                      color="primary"
                      :href="`${project.webUrl}/-/pipelines`"
                      target="_blank"
                  >
                    View All Pipelines
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <v-list dense>
                    <v-list-item
                        v-for="pipeline in projectDetails?.pipelines"
                        :key="pipeline.id"
                        :href="`${project.webUrl}/-/pipelines/${pipeline.id}`"
                        target="_blank"
                    >
                      <v-list-item-content>
                        <v-list-item-title class="subtitle-2">
                          Pipeline #{{ pipeline.id }}
                          <v-chip
                              x-small
                              :color="getPipelineStatusColor(pipeline.status)"
                              text-color="white"
                              class="ml-2"
                          >
                            {{ pipeline.status }}
                          </v-chip>
                        </v-list-item-title>
                        <v-list-item-subtitle class="caption">
                          {{ pipeline.ref }} • {{ formatDate(pipeline.created_at) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- 브랜치 목록 -->
          <v-row class="mt-4">
            <v-col cols="12">
              <v-card outlined>
                <v-card-title class="subtitle-1 d-flex align-center">
                  <v-icon left>mdi-source-branch</v-icon>
                  Branches
                  <v-spacer></v-spacer>
                  <v-btn
                      small
                      text
                      color="primary"
                      :href="`${project.webUrl}/-/branches`"
                      target="_blank"
                  >
                    View All Branches
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                      <tr>
                        <th>Name</th>
                        <th>Latest Commit</th>
                        <th>Protected</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="branch in projectDetails?.branches" :key="branch.name">
                        <td>
                          <a
                              :href="`${project.webUrl}/-/tree/${branch.name}`"
                              target="_blank"
                              class="text-decoration-none"
                          >
                            {{ branch.name }}
                          </a>
                        </td>
                        <td>
                          <a
                              :href="`${project.webUrl}/-/commit/${branch.commit?.id}`"
                              target="_blank"
                              class="text-decoration-none"
                          >
                            {{ branch.commit?.short_id }}
                            <span class="caption grey--text ml-2">
                                {{ branch.commit?.title }}
                              </span>
                          </a>
                        </td>
                        <td>
                          <v-icon small :color="branch.protected ? 'success' : 'grey'">
                            {{ branch.protected ? 'mdi-shield' : 'mdi-shield-outline' }}
                          </v-icon>
                        </td>
                      </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- 컨트리뷰터 -->
          <v-row class="mt-4">
            <v-col cols="12">
              <v-card outlined>
                <v-card-title class="subtitle-1">
                  <v-icon left>mdi-account-group</v-icon>
                  Contributors
                </v-card-title>
                <v-card-text>
                  <v-list dense>
                    <v-list-item
                        v-for="(contributor, index) in projectDetails?.extendedStats?.contributors"
                        :key="`${contributor.email}-${index}`"
                    >
                      <v-list-item-avatar size="32">
                        <v-img :src="getAvatarUrl(contributor.email)"></v-img>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title>{{ contributor.username }}</v-list-item-title>
                        <v-list-item-subtitle class="caption">
                          Commits: {{ contributor.commits }} •
                          Additions: +{{ contributor.additions }} •
                          Deletions: -{{ contributor.deletions }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'ProjectDetailsDialog',

  props: {
    value: Boolean,
    project: {
      type: Object,
      default: null
    },
    projectDetails: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    }
  },

  methods: {
    close() {
      this.dialog = false;
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    getMrStateColor(state) {
      const colors = {
        opened: 'primary',
        merged: 'success',
        closed: 'error'
      };
      return colors[state] || 'grey';
    },
    getPipelineStatusColor(status) {
      const colors = {
        success: 'success',
        failed: 'error',
        running: 'info',
        pending: 'warning',
        canceled: 'grey'
      };
      return colors[status] || 'grey';
    },
    // md5 대신 기본 아바타 URL 사용
    getAvatarUrl(email) {
      return `https://www.gravatar.com/avatar/0?d=identicon`;
    }
  }
};
</script>

<style scoped>
.stat-card {
  transition: all 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.v-list-item {
  transition: background-color 0.2s ease;
}

.v-list-item:hover {
  background-color: #f5f5f5;
}
</style>